import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import PurchaseInvoiceDetailModal from './components/PurchaseInvoiceDetailModal';
import BarcodePrintModal from '@/components/feature/barcode-print/BarcodePrintModal';
import { BarcodePrintItem } from '@/components/feature/barcode-print/Types';
import { useToast } from '@/contexts/ToastContext';
import { purchaseService } from '@/services/purchaseService';
import { isBackendAvailable } from '@/api/client';
import PrintPurchaseInvoiceView from './components/printDetails';
import { MODULES } from '@/utils/permissions.js';
import { useAuth } from '@/contexts/AuthContext';
import { deleteData } from '../../../services/FetchNodeServices.js';
import { useWarehouseStore } from '@/stores/warehouseStore';

const mapInvoice = (row: any): PurchaseInvoice => ({
  id: row.id,
  invoiceNo: row.invoiceNumber,
  date: new Date(row.invoiceDate).toLocaleDateString('en-IN'),
  createdAt: row.createdAt,
  partyName: row.supplierName || '-',
  warehouseName: row.warehouseName || '—',
  itemCount: row.itemCount || 0,
  grandTotal: Math.ceil(row.totalAmount || 0),
  status: row.status || 'SAVED',
  paymentStatus: row.paymentStatus || 'UNPAID',
  supplierId: row.supplierId || '',
  balanceDue: Math.ceil(row.balanceDue ?? row.totalAmount ?? 0),
  paidAmount: Math.ceil(row.paidAmount || 0),
  supplierInvoiceNo: row.supplierInvoiceNo || '',
  items: row.items || [],
  // TDS we deduct when paying this vendor — snapshot taken at invoice time,
  // drives the Record Payment page's TDS section.
  tdsApplicable: Boolean(row.tdsApplicable),
  tdsSection: row.tdsSection || null,
  tdsRate: row.tdsRate !== null && row.tdsRate !== undefined ? Number(row.tdsRate) : null,
  tdsAmount: row.tdsAmount ? Number(row.tdsAmount) : 0,
  expectedPayment: row.expectedPayment ? Number(row.expectedPayment) : undefined,
  tdsPanAtInvoice: row.tdsPanAtInvoice || null,
  tcsApplicable: Boolean(row.tcsApplicable),
  tcsRate: row.tcsRate !== null && row.tcsRate !== undefined ? Number(row.tcsRate) : null,
  tcsAmount: row.tcsAmount ? Number(row.tcsAmount) : 0,
});

type PurchaseInvoice = {
  id: string;
  invoiceNo: string;
  date: string;
  createdAt?: string;
  partyName: string;
  warehouseName: string;
  itemCount: number;
  grandTotal: number;
  status: string;
  paymentStatus: string;
  supplierId: string;
  balanceDue: number;
  paidAmount: number;
  supplierInvoiceNo?: string;
  items: any[];
  tdsApplicable?: boolean;
  tdsSection?: string | null;
  tdsRate?: number | null;
  tdsAmount?: number;
  expectedPayment?: number;
  tdsPanAtInvoice?: string | null;
  tcsApplicable?: boolean;
  tcsRate?: number | null;
  tcsAmount?: number;
};

function formatINR(n?: number) {
  const rounded = Math.ceil(n ?? 0);
  return `₹${rounded.toLocaleString('en-IN')}`;
}

// ─── ✅ Status Styles with DUPLICATE ──────────────────────────────────────
const STATUS_STYLE: Record<string, string> = {
  SAVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-red-50 text-red-600 border-red-200',
  DUPLICATE: 'bg-purple-50 text-purple-700 border-purple-200', // ✅ NEW
};

const PAYMENT_STYLE: Record<string, string> = {
  PAID: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  UNPAID: 'bg-red-50 text-red-600 border-red-200',
  PARTIAL: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function PurchaseInvoiceListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { selectedWarehouseId } = useWarehouseStore();
  const [search, setSearch] = useState('');
  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] =
    useState<PurchaseInvoice | null>(null);
  const [barcodePrintItems, setBarcodePrintItems] = useState<
    BarcodePrintItem[]
  >([]);
  const [barcodePrintOpen, setBarcodePrintOpen] = useState(false);
  const [printInvoiceId, setPrintInvoiceId] = useState<string | null>(null);
  const { hasPermission } = useAuth();
  const canCreateInvoice = hasPermission(MODULES.PURCHASE_INVOICE, 'create');
  const canEditInvoice = hasPermission(MODULES.PURCHASE_INVOICE, 'edit');
  const canDeleteInvoice = hasPermission(MODULES.PURCHASE_INVOICE, 'delete');
  const canCreatePayment = hasPermission(MODULES.PURCHASE_PAYMENT, 'create');
  const [tdsFilter, setTdsFilter] = useState<'ALL' | 'TDS_APPLICABLE' | 'NO_TDS'>('ALL');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const filtered = invoices.filter((r) => {
    const matchSearch =
      r.invoiceNo?.toLowerCase().includes(search.toLowerCase()) ||
      r.partyName?.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (tdsFilter === 'TDS_APPLICABLE') return Boolean(r.tdsApplicable || r.tcsApplicable);
    if (tdsFilter === 'NO_TDS') return !r.tdsApplicable && !r.tcsApplicable;
    return true;
  });

  const total = invoices.reduce((s, r) => s + r.grandTotal, 0);

  useEffect(() => {
    let mounted = true;

    const loadInvoices = async () => {
      try {
        const params: any = {};
        if (selectedWarehouseId && selectedWarehouseId !== 'ALL') {
          params.warehouse_id = selectedWarehouseId;
        }
        const data = await purchaseService.listInvoices(params);
        const mapped: PurchaseInvoice[] = (data || []).map((row: any) => mapInvoice(row));

        if (mounted) setInvoices(mapped);
      } catch (err) {
        console.error('Invoice load error:', err);
      }
    };

    loadInvoices();

    return () => {
      mounted = false;
    };
  }, [selectedWarehouseId, location]);

  useEffect(() => {
    const navState = location.state as { openInvoiceId?: string; openInvoiceNo?: string } | null;
    if (!navState?.openInvoiceId && !navState?.openInvoiceNo) return;
    if (invoices.length === 0) return;

    const target = invoices.find((row) =>
      (navState.openInvoiceId && row.id === navState.openInvoiceId) ||
      (navState.openInvoiceNo && row.invoiceNo === navState.openInvoiceNo),
    );

    if (!target) {
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    setSelectedInvoice(target);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, invoices]);

  // ─── Edit Handler ────────────────────────────────────────────────────────
  const handleEdit = async (row: PurchaseInvoice, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const response = await purchaseService.getInvoiceForEdit(row.id);
      const full = response.data || response;

      navigate('/purchase/invoices/new', {
        state: {
          editMode: true,
          invoiceId: row.id,
          prefill: {
            supplierId: full.supplierId || full.supplier?.id,
            supplierName: full.supplierName || full.supplier?.name || '',
            supplierGstin: full.supplierGstin || full.supplier?.gstin || '',
            supplierPhone: full.supplierPhone || full.supplier?.phone || '',
            supplierStateCode:
              full.supplierStateCode || full.supplier?.stateCode || '',
            supplierAddress:
              full.supplierBillingAddress || full.supplier?.address || '',
            warehouseId: full.warehouseId || full.warehouse?.id,
            warehouseName: full.warehouseName || full.warehouse?.name || '',
            invoiceNumber: full.invoiceNumber,
            supplierInvoiceNo: full.supplierInvoiceNo || '',
            invoiceDate: full.invoiceDate?.slice(0, 10) || '',
            notes: full.notes || '',
            purchaseMode: full.purchaseMode || 'CASH',
            paymentStatus: full.paymentStatus || 'UNPAID',
            paidAmount: full.paidAmount || 0,
            balanceDue: full.balanceDue ?? full.totalAmount ?? 0,
            items: (full.items || []).map((item: any) => ({
              id: item.id || crypto.randomUUID(),
              itemId: item.itemId || item.item_id,
              itemName: item.itemName || item.item_name,
              brand: item.brand || '',
              hsnCode: item.hsnCode || item.hsn_code || '',
              qty: Number(item.qty || 0),
              unit: item.unitName || item.unit_name || item.unit || 'Pcs',
              unitId: item.unitId || item.unit_id || '',
              purRate: Number(item.rate || item.purRate || 0),
              taxRate: Number(
                item.taxRate || item.tax_rate || item.gst_rate || 0,
              ),
              mrp: Number(item.mrp || 0),
              saleRate: Number(item.saleRate || 0),
              amount: Number(item.qty || 0) * Number(item.rate || 0),
              discountPct: Number(item.discountPct || item.discount_pct || 0),
              isKnownItem: true,
              articleNo: item.articleNo || item.article_no || '',
              group: item.group || '',
              size: item.size || '',
            })),
          },
        },
      });
    } catch (err: any) {
      console.error('❌ Edit error:', err);
      toast.error(err?.message ?? 'Failed to load invoice for editing');
    }
  };

  const handleRecordPayment = (
    invoice: PurchaseInvoice,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    navigate('/purchase/payments/new', {
      state: {
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNo,
        supplierId: invoice.supplierId,
        supplierName: invoice.partyName,
        invoiceAmount: invoice.grandTotal,
        balanceDue: invoice.balanceDue ?? invoice.grandTotal,
        invoiceDate: invoice.date,
        tdsApplicable: invoice.tdsApplicable,
        tdsSection: invoice.tdsSection,
        tdsRate: invoice.tdsRate,
        tdsAmount: invoice.tdsAmount,
        expectedPayment: invoice.expectedPayment,
        tdsPanAtInvoice: invoice.tdsPanAtInvoice,
      },
    });
  };

  const handleDuplicate = async (
    invoice: PurchaseInvoice,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    try {
      const res = await purchaseService.duplicateInvoice(invoice.id);
      const newInvoice = res;
      toast.success(`Created ${newInvoice.invoiceNumber}`);
      setInvoices((prev) => [mapInvoice(newInvoice), ...prev]);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Duplicate failed');
    }
  };

  // ── Generate Gate Pass ──────────────────────────────────────────────────────
  const handleGenerateGP = async (invoice: PurchaseInvoice) => {
    setSelectedInvoice(null);

    let fullInvoice = invoice;
    if (!invoice.items || invoice.items.length === 0) {
      try {
        const res = await purchaseService.getInvoice(invoice.id);
        if (res) fullInvoice = res;
      } catch (err) {
        console.error('Failed to fetch invoice items:', err);
      }
    }

    const prefill = {
      partyName: fullInvoice.partyName || fullInvoice.supplierName || '',
      linkedDocType: 'PURCHASE_INVOICE',
      linkedDocNumber: fullInvoice.invoiceNo || fullInvoice.invoiceNumber,
      notes: `Prefilled from Purchase Invoice ${fullInvoice.invoiceNo || fullInvoice.invoiceNumber}`,
      items: (fullInvoice.items ?? []).map((i: any) => ({
        itemName: i.itemName || i.item_name,
        qty: Number(i.qty || i.quantity || 0),
        unit: i.unitName || i.unit || 'Pcs',
      })),
    };

    navigate('/inventory/gate-pass/inward', { state: { prefill } });
  };

  const handlePrintBarcodes = async (invoice: PurchaseInvoice) => {
    try {
      const fullInvoice = await purchaseService.getInvoice(invoice.id);
      const items: BarcodePrintItem[] = (fullInvoice.items || []).flatMap(
        (item: any) => {
          const isSerial = Boolean(
            item.is_serial_tracked ||
            item.isSerialTracked ||
            (item.serials && item.serials.length > 0)
          );
          const isBatch = Boolean(
            item.is_batch_tracked ||
            item.isBatchTracked ||
            item.batch_number ||
            item.batchNumber
          );

          if (item.serials && Array.isArray(item.serials) && item.serials.length > 0) {
            return item.serials.map((s: any) => ({
              itemId: item.itemId,
              itemName: item.itemName,
              brand: item.brand ?? '—',
              category: item.categoryName ?? '—',
              barcode: item.barcode ?? null,
              mrp: item.mrp ?? 0,
              saleRate: item.rate,
              hsnCode: item.hsnCode,
              articleNo: item.articleNo,
              labelQty: 1,
              isSerialTracked: true,
              isBatchTracked: isBatch,
              serialNumber: typeof s === 'string' ? s : (s.serial_number || s.serialNumber),
              batchNumber: item.batch_number || item.batchNumber || (s.batch_number || s.batchNumber),
            }));
          }

          return [
            {
              itemId: item.itemId,
              itemName: item.itemName,
              brand: item.brand ?? '—',
              category: item.categoryName ?? '—',
              barcode: item.barcode ?? null,
              mrp: item.mrp ?? 0,
              saleRate: item.rate,
              hsnCode: item.hsnCode,
              articleNo: item.articleNo,
              labelQty: item.qty,
              isSerialTracked: isSerial,
              isBatchTracked: isBatch,
              batchNumber: item.batch_number || item.batchNumber,
            },
          ];
        },
      );
      setBarcodePrintItems(items);
      setBarcodePrintOpen(true);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load barcode data');
    }
  };

  const handleDeletePo = (row: PurchaseInvoice) => {
    setDeleteTarget(row);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await purchaseService.deleteInvoice(deleteTarget.id);
      setInvoices((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      toast.success('Invoice deleted successfully');
      setDeleteTarget(null);
      setSelectedInvoice(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete invoice');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] h-[calc(100vh-4rem)] flex flex-col">
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">
              Purchase Invoices
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {invoices.length} invoices &nbsp;·&nbsp; Total {formatINR(total)}
            </p>
          </div>
          {canCreateInvoice && (
            <button
              onClick={() => navigate('/purchase/invoices/new')}
              className="flex items-center gap-2 px-4 py-2 bg-[#4f46e5] text-white text-sm font-medium rounded-lg hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-add-line" /> New Purchase
            </button>
          )}
        </div>

        <div className="bg-white border border-[#e2e8f0] rounded-xl flex-1 flex flex-col min-h-0">
          <div className="p-4 border-b border-[#e2e8f0] shrink-0 flex items-center justify-between gap-4 flex-wrap">
            <div className="relative w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search invoices..."
                className="w-full h-9 pl-8 pr-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
              />
            </div>

            <div className="flex items-center gap-3">
              <select
                value={tdsFilter}
                onChange={(e) =>
                  setTdsFilter(e.target.value as 'ALL' | 'TDS_APPLICABLE' | 'NO_TDS')
                }
                className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm text-slate-600 bg-white focus:outline-none focus:border-[#4f46e5]"
              >
                <option value="ALL">All TDS</option>
                <option value="TDS_APPLICABLE">TDS Applicable</option>
                <option value="NO_TDS">No TDS</option>
              </select>
            </div>
          </div>

          <div className="flex-1 overflow-auto hover-scrollbar bg-white min-h-0 rounded-b-xl">
            <table className="w-full min-w-[1200px] text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  {[
                    'Invoice No',
                    'Date/Time',
                    'Supplier',
                    'Warehouse',
                    'Items',
                    'Payment',
                    'Amount',
                    'TDS/TCS',
                    'Balance Due',
                    'Status',
                    'Actions',
                  ].map((h) => (
                    <th
                      key={h}
                      className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] whitespace-nowrap ${h === 'Amount' || h === 'Balance Due' ? 'text-right' : ''}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr
                    key={row.id}
                    className={`border-b border-slate-50 hover:bg-indigo-50/30 cursor-pointer transition-colors ${i % 2 === 1 ? 'bg-[#f8fafc]' : ''}`}
                    onClick={() => setSelectedInvoice(row)}
                  >
                    <td className="px-4 py-3 font-semibold text-[#4f46e5] whitespace-nowrap">
                      <span>{row.invoiceNo}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-xs text-[#1e293b]">{row.date}</p>
                      {row.createdAt && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {new Date(row.createdAt).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-[#1e293b]">
                      {row.partyName}
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {row.warehouseName}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-500">
                      {row.itemCount}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${PAYMENT_STYLE[row.paymentStatus]}`}
                      >
                        {row.paymentStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-[#1e293b] whitespace-nowrap">
                      {formatINR(row.grandTotal)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-col gap-1 items-start">
                        {row.tdsApplicable ? (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap inline-flex items-center gap-1 ${
                              row.tdsRate && row.tdsRate >= 20
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}
                          >
                            TDS {row.tdsRate}% {row.tdsRate && row.tdsRate >= 20 ? '(no PAN)' : (row.tdsSection ? `· ${row.tdsSection}` : '')}
                          </span>
                        ) : null}
                        {row.tcsApplicable ? (
                          <span className="text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap inline-flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200">
                            TCS {formatINR(row.tcsAmount || 0)}
                          </span>
                        ) : null}
                        {!row.tdsApplicable && !row.tcsApplicable ? (
                          <span className="text-gray-300">—</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(row.balanceDue ?? 0) > 0 ? (
                        <span className="font-semibold text-red-600">
                          {formatINR(row.balanceDue ?? 0)}
                        </span>
                      ) : (
                        <span className="text-emerald-600 font-medium text-xs">
                          Cleared
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${STATUS_STYLE[row.status]}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedInvoice(row)}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#4f46e5] rounded cursor-pointer"
                          title="View"
                        >
                          <i className="ri-eye-line text-sm" />
                        </button>

                        {/* ✅ Edit allowed for ALL statuses except CANCELLED */}
                        {row.status !== 'CANCELLED' && canEditInvoice && (
                          <button
                            onClick={(e) => handleEdit(row, e)}
                            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-indigo-600 rounded cursor-pointer"
                            title="Edit Invoice"
                          >
                            <i className="ri-pencil-line text-sm" />
                          </button>
                        )}

                        {/* Delete */}
                        {row.status !== 'COMPLETED' &&
                          canDeleteInvoice &&
                          row.paidAmount <= 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePo(row);
                              }}
                              title="Delete"
                              className="w-7 h-7 flex items-center justify-center rounded-lg
                                      text-slate-400 hover:text-red-500 hover:bg-red-50
                                      transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          )}
                        {row.status !== 'CANCELLED' && hasPermission(MODULES.GATE_PASS_INWARD, 'create') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateGP(row);
                            }}
                            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-purple-600 rounded cursor-pointer"
                            title="Generate GP"
                          >
                            <i className="ri-ticket-2-line text-sm" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPrintInvoiceId(row.id);
                          }}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#4f46e5] rounded cursor-pointer"
                          title="Print Invoice"
                        >
                          <i className="ri-printer-line text-sm" />
                        </button>
                        {row.status !== 'CANCELLED' &&
                          (row.paymentStatus === 'UNPAID' ||
                            row.paymentStatus === 'PARTIAL') &&
                          canCreatePayment && (
                            <button
                              onClick={(e) => handleRecordPayment(row, e)}
                              className="flex items-center gap-1 h-6 px-2 text-xs font-medium text-green-700 border border-green-200 bg-green-50 hover:bg-green-100 rounded cursor-pointer whitespace-nowrap transition-colors"
                              title="Record Payment"
                            >
                              <i className="ri-money-rupee-circle-line text-xs" />
                              Pay
                            </button>
                          )}
                        {canCreateInvoice && (
                          <button
                            onClick={(e) => handleDuplicate(row, e)}
                            className="flex items-center gap-1 h-6 px-2 text-xs font-medium text-indigo-700 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 rounded cursor-pointer whitespace-nowrap transition-colors"
                            title="Duplicate Invoice"
                          >
                            <i className="ri-file-copy-line text-xs" />
                            Dup
                          </button>
                        )}
                        <button
                          onClick={() => handlePrintBarcodes(row)}
                          className="flex items-center gap-1 h-6 px-2 text-xs font-medium text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded cursor-pointer whitespace-nowrap transition-colors"
                          title="Print Barcodes"
                        >
                          <i className="ri-barcode-line text-xs" />
                          Barcodes
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {printInvoiceId && (
            <PrintPurchaseInvoiceView
              invoiceId={printInvoiceId}
              onClose={() => setPrintInvoiceId(null)}
            />
          )}

          {filtered.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <i className="ri-file-list-3-line text-4xl mb-2 block" />
              <p className="text-sm">No invoices found</p>
            </div>
          )}
        </div>
      </div>

      {selectedInvoice && (
        <PurchaseInvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          handleGenerateGP={handleGenerateGP}
        />
      )}

      <BarcodePrintModal
        open={barcodePrintOpen}
        onClose={() => setBarcodePrintOpen(false)}
        items={barcodePrintItems}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 shrink-0">
                <i className="ri-delete-bin-line text-red-600 text-xl" />
              </div>
              <div>
                <p className="text-sm font-bold text-[#1e293b]">
                  Delete {deleteTarget.invoiceNo}?
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  This will reverse stock and cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 h-9 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex-1 h-9 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 cursor-pointer"
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
